import React, { useState } from "react";
import { CalendarClock, Volume2, Pencil, Ban, Trash2, Users, ChevronDown, ChevronUp } from "lucide-react";
import RsvpButtons from "./RsvpButtons";
import MeetingResponses from "./MeetingResponses";
import { countdown, formatTime, formatWhen, phaseOf } from "./meetingUtils";

const PHASE_CHIP = {
  upcoming: ["Scheduled", "border-[var(--ff-border)] text-[var(--ff-text-dim)]"],
  soon: ["Starting soon", "border-emerald-500 text-emerald-400"],
  live: ["In progress", "border-emerald-500 bg-emerald-500 text-black"],
  ended: ["Ended", "border-[var(--ff-border)] text-[var(--ff-text-dim)]"],
  canceled: ["Canceled", "border-[var(--ff-red)] text-[var(--ff-red)]"],
};

function ManagerActions({ m, onEdit, onCancel, onDelete }) {
  return (
    <div className="flex items-center gap-3">
      {m.status === "scheduled" && (
        <button onClick={() => onEdit(m)} className="text-[var(--ff-text-dim)] hover:text-white" title="Edit" data-testid={`meeting-edit-${m.id}`}><Pencil size={14} /></button>
      )}
      {m.status === "scheduled" && (
        <button onClick={() => onCancel(m)} className="text-[var(--ff-text-dim)] hover:text-[var(--ff-red)]" title="Cancel meeting" data-testid={`meeting-cancel-${m.id}`}><Ban size={14} /></button>
      )}
      <button onClick={() => onDelete(m)} className="text-[var(--ff-red)]" title="Delete" data-testid={`meeting-delete-${m.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

export default function MeetingCard({ m, now, tz, canRsvp, canManage, canSeeResponses, onRsvp, onEdit, onCancel, onDelete }) {
  const [open, setOpen] = useState(false);
  const phase = phaseOf(m, now);
  const [chip, chipCls] = PHASE_CHIP[phase];
  const live = phase === "soon" || phase === "live";
  const c = m.counts;
  return (
    <div className={`surface-card p-6 space-y-4 ${phase === "canceled" ? "opacity-60" : ""}`} data-testid={`meeting-card-${m.id}`}>
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`font-mono-ui text-[0.6rem] uppercase tracking-[0.14em] px-2 py-0.5 border ${chipCls}`} data-testid={`meeting-phase-${m.id}`}>{chip}</span>
            {phase === "upcoming" && <span className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] uppercase tracking-[0.12em]">{countdown(m.start_at, now)}</span>}
          </div>
          <div className="font-display text-2xl leading-tight" data-testid={`meeting-title-${m.id}`}>{m.title}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 font-mono-ui text-xs text-[var(--ff-text-dim)]">
            <span className="inline-flex items-center gap-1.5 text-white" data-testid={`meeting-when-${m.id}`}>
              <CalendarClock size={12} className="text-[var(--ff-red)]" /> {formatWhen(m.start_at, tz)} – {formatTime(m.end_at, tz)}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid={`meeting-room-${m.id}`}>
              <Volume2 size={12} /> {m.room?.channel_id ? m.room.channel_name || "Voice channel" : "No room set"}{m.room?.channel_id && !m.room.is_default ? " (this meeting only)" : ""}
            </span>
          </div>
        </div>
        {canManage && <ManagerActions m={m} onEdit={onEdit} onCancel={onCancel} onDelete={onDelete} />}
      </div>

      {m.agenda && <p className="text-sm text-[var(--ff-text-dim)] whitespace-pre-line" data-testid={`meeting-agenda-${m.id}`}>{m.agenda}</p>}

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-4 border-t border-[var(--ff-border)]">
        <div className="flex flex-wrap items-center gap-3">
          {canRsvp && m.status === "scheduled" && phase !== "ended" && <RsvpButtons meeting={m} onRsvp={onRsvp} />}
          {live && m.room?.join_url && m.my_rsvp?.status !== "unavailable" && (
            <a href={m.room.join_url} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid={`meeting-join-${m.id}`}><Volume2 size={12} /> Join VC</a>
          )}
        </div>
        <button
          onClick={() => canSeeResponses && setOpen((o) => !o)}
          className={`inline-flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.12em] ${canSeeResponses ? "hover:text-white" : "cursor-default"} text-[var(--ff-text-dim)]`}
          data-testid={`meeting-counts-${m.id}`}
        >
          <Users size={12} />
          <span className="text-emerald-400">{c.available} available</span> ·
          <span className="text-[var(--ff-red)]">{c.unavailable} not</span> ·
          <span>{c.pending} waiting</span>
          {canSeeResponses && (open ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
        </button>
      </div>
      {open && <MeetingResponses meetingId={m.id} />}
    </div>
  );
}
